import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useStudents, studentsQuery } from "../../../data/hooks/useStudents";
import { useSessions, sessionsQuery } from "../../../data/hooks/useSessions";
import useBehaviorTypes from "../../../data/hooks/useBehaviorTypes";
import useLevels from "../../../data/hooks/useLevels";
import { ToastMessage } from "../../../Components/Toast/ToastMessage";
import { useData } from "../../../context/StoreContext";
import { ROLES } from "../../../utils/roles";
import SessionsRepository from "../../../data/repository/SessionsRepository";
import SessionsService from "../../../data/services/SessionsService";
import { findById } from "../../../Mapper/StudentMapper";

import ErrorDisplay from "../../../Components/Error/ErrorDisplay";
import Loading from "../../../Components/Loading/Loading";

import { getAccuracyCategory, getStudentFullName } from "./constants";
import AccuracyStep from "./AccuracyStep";
import FluencyStep from "./FluencyStep";
import ResultsStep from "./ResultsStep";
import QuestionsStep from "./QuestionsStep";
import AssessmentResultsStep from "./AssessmentResultsStep";
import { EvaluationHeader, EvaluationStepper } from "./components";

import {
  useClassrooms,
  classroomsQuery,
} from "../../../data/hooks/useClassrooms";
import { useSections, sectionsQuery } from "../../../data/hooks/useSections";

const STEPS = [
  "accuracy",
  "fluency",
  "results",
  "questions",
  "assessment-results",
];

const extractAnsweredOpenQuestions = (sessionsData) => {
  const questionSets = sessionsData?.assessmentText?.questionSets || [];
  const resultsSets = sessionsData?.comprehension?.questionSetsResults || [];

  return questionSets.flatMap((set, setIdx) => {
    const setResults = resultsSets.find((r) => r.questionSetIndex === setIdx);

    return (set.questions || [])
      .filter((q) => q.type === "open")
      .map((q) => {
        const answerObj = setResults?.answers?.find(
          (a) => a.questionId === q.id,
        );
        const rawAnswer = answerObj?.answer;
        const answerText =
          typeof rawAnswer === "object" && rawAnswer !== null
            ? rawAnswer.answer
            : rawAnswer;

        return {
          question: q,
          answer: answerObj,
          setIdx,
          answerText,
          questionId: q.id,
          score: answerObj?.score ?? 0,
          feedback: answerObj?.feedback || "",
        };
      })
      .filter((item) => item.answerText?.trim());
  });
};

const SessionEvaluationPage = () => {
  const sessionsService = useMemo(
    () => new SessionsService(new SessionsRepository()),
    [],
  );
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useData();

  const requestedStep = new URLSearchParams(location.search).get("step");
  const step = STEPS.includes(requestedStep) ? requestedStep : STEPS[0];

  const navigateToStep = (nextStep) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("step", nextStep);

    history.replace({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
      hash: location.hash,
      state: location.state,
    });
  };

  const studentId = params.student;
  const sessionId = params.session;
  const classId = params.classroom;
  const sectionId = params.section;
  const schoolId = params.school || currentUser?.school?._id;
  const userRole = currentUser?.role;

  const { isSessionsLoading, sessionsError, sessionsData } = useSessions(
    sessionsQuery.report,
    sessionId,
  );

  const { isStudentsLoading, studentsError, studentsData } = useStudents(
    studentsQuery.byId,
    studentId,
  );

  const { isLevelsLoading, levelsError, levelsData } = useLevels();

  const { isBehaviorTypesLoading, behaviorTypesError, behaviorTypesData } =
    useBehaviorTypes();

  const { classroomsData } = useClassrooms(classroomsQuery.byId, classId);
  const { sectionsData } = useSections(sectionsQuery.byId, sectionId);

  const session = useMemo(
    () => sessionsData?.session || sessionsData,
    [sessionsData],
  );

  const effectiveStudentData = studentsData || session?.student;
  const effectiveClassroomData = classroomsData || session?.classroom;
  const effectiveSectionData = sectionsData || session?.section;

  const [localBehaviors, setLocalBehaviors] = useState([]);
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [fluencyScore, setFluencyScore] = useState(0);
  const [fluencyNotes, setFluencyNotes] = useState("");
  const [evaluations, setEvaluations] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const answeredOpenQuestions = useMemo(() => {
    return extractAnsweredOpenQuestions(session);
  }, [session]);

  const incorrectWords = localBehaviors.length;

  const { totalWords, accuracyPercent, activeCategory } = useMemo(() => {
    const text = session?.assessmentText?.text || session?.text || "";
    const words = text
      ? text.split(/\s+/).filter((w) => w.trim() !== "").length
      : 0;
    const accPercent =
      words <= 0
        ? 100
        : Number((((words - incorrectWords) / words) * 100).toFixed(1));
    return {
      totalWords: words,
      accuracyPercent: accPercent,
      activeCategory: getAccuracyCategory(accPercent),
    };
  }, [session, incorrectWords]);

  const comprehensionScore = useMemo(() => {
    return (session?.comprehension?.questionSetsResults || []).reduce(
      (totalSet, setResult) => {
        const setScore = (setResult.answers || []).reduce((totalAns, ans) => {
          const ev = evaluations.find(
            (e) =>
              e.questionId === ans.questionId &&
              e.setIdx === setResult.questionSetIndex,
          );
          return totalAns + Number(ev ? ev.score : ans.score || 0);
        }, 0);
        return totalSet + setScore;
      },
      0,
    );
  }, [session, evaluations]);

  useEffect(() => {
    setIsInitialized(false);
  }, [sessionId]);

  useEffect(() => {
    const loadedSessionId = session?._id || session?.id;
    if (session && loadedSessionId === sessionId && !isInitialized) {
      setLocalBehaviors(session?.reading?.behaviors || []);
      setNotes(
        session?.reading?.accuracyNotes || session?.reading?.notes || "",
      );
      setFluencyScore(session?.results?.fluencyScore || 0);
      setFluencyNotes(
        session?.results?.fluencyNotes || session?.reading?.notes || "",
      );

      setEvaluations(
        answeredOpenQuestions.map((item) => ({
          questionId: item.questionId,
          setIdx: item.setIdx,
          score: item.score,
          feedback: item.feedback,
        })),
      );
      setIsInitialized(true);
    }
  }, [session, answeredOpenQuestions, isInitialized, sessionId]);

  const isLoading =
    isSessionsLoading ||
    isLevelsLoading ||
    isBehaviorTypesLoading ||
    isStudentsLoading;

  const isError =
    sessionsError || studentsError || levelsError || behaviorTypesError;

  if (isError) {
    return (
      <ErrorDisplay
        userMessage="حدث خطأ أثناء تحميل البيانات"
        errorMessage={isError.message || String(isError)}
      />
    );
  }

  if (
    isLoading ||
    !session ||
    !effectiveStudentData ||
    !levelsData ||
    !behaviorTypesData
  ) {
    return <Loading />;
  }

  const studentAudio = session?.reading?.recording;

  const handleApplyBehavior = (wordIndex, behaviorId) => {
    setLocalBehaviors((prev) => {
      const filtered = prev.filter((b) => b.index !== wordIndex);
      const existing = prev.find((b) => b.index === wordIndex);
      return existing?.behavior === behaviorId
        ? filtered
        : [...filtered, { index: wordIndex, behavior: behaviorId }];
    });
  };

  const handleEvaluationChange = (questionId, field, value) => {
    setEvaluations((prev) => {
      const exists = prev.some((item) => item.questionId === questionId);
      if (exists) {
        return prev.map((item) =>
          item.questionId === questionId ? { ...item, [field]: value } : item,
        );
      }
      const qItem = answeredOpenQuestions.find(
        (item) => item.questionId === questionId,
      );
      return [
        ...prev,
        {
          questionId,
          setIdx: qItem?.setIdx ?? 0,
          score: field === "score" ? value : 0,
          feedback: field === "feedback" ? value : "",
          [field]: value,
        },
      ];
    });
  };

  const navigateToStudentDashboard = () => {
    const backUrl =
      userRole === ROLES.TEACHER
        ? `/classrooms/${classId}/sections/${sectionId}/students/${studentId}`
        : `/schools/${schoolId}/classrooms/${classId}/sections/${sectionId}/students/${studentId}`;
    history.push(backUrl);
  };

  const executeSaveOperation = async ({
    loadingMessage,
    successMessage,
    errorMessage,
    errorLog,
    saveFn,
    onSuccess,
  }) => {
    setIsSaving(true);
    const toastId = ToastMessage.loading(loadingMessage);
    try {
      await saveFn();
      ToastMessage.dismiss(toastId);
      ToastMessage.success(successMessage);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(errorLog, err);
      ToastMessage.dismiss(toastId);
      ToastMessage.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFluencyStepNext = () => {
    const payload = {
      duration: session?.reading?.duration || 1,
      behaviors: localBehaviors.map((b) => ({
        index: b.index,
        behavior: b.behavior,
      })),
      fluencyScore: fluencyScore,
    };

    executeSaveOperation({
      loadingMessage: "جاري حفظ تقرير القراءة...",
      successMessage: "تم حفظ تقرير القراءة بنجاح",
      errorMessage: "حدث خطأ أثناء حفظ تقرير القراءة",
      errorLog: "Error saving reading report:",
      saveFn: () => sessionsService.submitReadingReport(sessionId, payload),
      onSuccess: () => navigateToStep("results"),
    });
  };

  const handleResultsStepNext = () => {
    const payload = {
      reading: {
        accuracyNotes: notes,
      },
    };

    executeSaveOperation({
      loadingMessage: "جاري حفظ الملاحظات...",
      successMessage: "تم حفظ الملاحظات بنجاح",
      errorMessage: "حدث خطأ أثناء حفظ الملاحظات",
      errorLog: "Error saving notes:",
      saveFn: () => sessionsService.updateSession(sessionId, payload),
      onSuccess: () => navigateToStep("questions"),
    });
  };

  const handleSave = () => {
    const updatedReading = {
      ...session?.reading,
      behaviors: localBehaviors,
      accuracyNotes: notes,
      notes: fluencyNotes,
    };

    const updatedQuestionSetsResults = (
      session?.comprehension?.questionSetsResults || []
    ).map((setResult) => {
      const updatedAnswers = (setResult.answers || []).map((ans) => {
        const ev = evaluations.find(
          (e) =>
            e.questionId === ans.questionId &&
            e.setIdx === setResult.questionSetIndex,
        );
        if (ev) {
          return {
            ...ans,
            score: ev.score,
            feedback: ev.feedback,
          };
        }
        return ans;
      });
      return {
        ...setResult,
        answers: updatedAnswers,
      };
    });

    const updatedResults = {
      ...session?.results,
      fluencyScore: fluencyScore,
      fluencyNotes: fluencyNotes,
      accuracyScore: accuracyPercent,
    };

    const updatedComprehension = {
      ...session?.comprehension,
      questionSetsResults: updatedQuestionSetsResults,
    };

    executeSaveOperation({
      loadingMessage: "جاري حفظ التقييم...",
      successMessage: "تم حفظ التقييم بنجاح",
      errorMessage: "حدث خطأ أثناء حفظ التقييم",
      errorLog: "Error saving session evaluation",
      saveFn: async () => {
        if (evaluations && evaluations.length > 0) {
          const scoreAnswersDto = {
            scores: evaluations.map((ev) => ({
              questionId: ev.questionId,
              score: Number(ev.score),
            })),
          };
          await sessionsService.scoreAnswers(sessionId, scoreAnswersDto);
        }
        await sessionsService.updateSession(sessionId, {
          reading: updatedReading,
          results: updatedResults,
          comprehension: updatedComprehension,
        });
        await sessionsService.evaluateSession(sessionId);
      },
      onSuccess: () => navigateToStep("assessment-results"),
    });
  };

  const handleDecideSession = (decisionData) => {
    const payload = {
      decision: {
        type: decisionData.type,
        ...(decisionData.levelId ? { levelId: decisionData.levelId } : {}),
      },
    };

    executeSaveOperation({
      loadingMessage: "جاري حفظ القرار...",
      successMessage: "تم حفظ القرار بنجاح",
      errorMessage: "حدث خطأ أثناء حفظ القرار",
      errorLog: "Error saving session decision:",
      saveFn: () => sessionsService.decideSession(sessionId, payload),
      onSuccess: navigateToStudentDashboard,
    });
  };

  const handleExit = navigateToStudentDashboard;

  const handleBack = () => {
    const currentIndex = STEPS.indexOf(step);
    if (currentIndex > 0) {
      navigateToStep(STEPS[currentIndex - 1]);
    } else {
      handleExit();
    }
  };

  const currentStepIndex = STEPS.indexOf(step);
  const stepperIndex =
    currentStepIndex <= 2 ? 0 : currentStepIndex === 3 ? 1 : 2;

  const studentName = getStudentFullName(effectiveStudentData);
  const bookTitle = session?.assessmentText?.title || session?.title || "";
  const studentLevelCode =
    findById(levelsData, effectiveStudentData?.levels?.current?._id)?.code ||
    effectiveStudentData?.levels?.current?.code;

  return (
    <div
      className="d-flex flex-column min-vh-100"
      style={{ backgroundColor: "#f8faf9" }}
    >
      <EvaluationHeader
        studentName={studentName}
        bookTitle={bookTitle}
        currentLevel={studentLevelCode}
        onClose={handleExit}
      />

      <EvaluationStepper currentStepIndex={stepperIndex} />

      <div
        className="flex-grow-1 overflow-auto p-4 w-100"
        style={{ minHeight: 0, paddingBottom: "100px" }}
      >
        {step === "accuracy" && (
          <AccuracyStep
            sessionsData={session}
            studentAudio={studentAudio}
            notes={notes}
            calculatedWordCount={totalWords}
            behaviorTypesData={behaviorTypesData}
            localBehaviors={localBehaviors}
            handleApplyBehavior={handleApplyBehavior}
            onBack={handleBack}
            onNext={() => navigateToStep("fluency")}
          />
        )}

        {step === "fluency" && (
          <FluencyStep
            fluencyScore={fluencyScore}
            setFluencyScore={setFluencyScore}
            fluencyNotes={fluencyNotes}
            setFluencyNotes={setFluencyNotes}
            onBack={handleBack}
            onNext={handleFluencyStepNext}
            isSaving={isSaving}
          />
        )}

        {step === "results" && (
          <ResultsStep
            totalWords={totalWords}
            incorrectWords={incorrectWords}
            accuracyPercent={accuracyPercent}
            activeCategory={activeCategory}
            fluencyScore={fluencyScore}
            notes={notes}
            setNotes={setNotes}
            onBack={handleBack}
            onNext={handleResultsStepNext}
            isSaving={isSaving}
          />
        )}

        {step === "questions" && (
          <QuestionsStep
            answeredOpenQuestions={answeredOpenQuestions}
            evaluations={evaluations}
            handleEvaluationChange={handleEvaluationChange}
            handleSave={handleSave}
            handleExit={handleExit}
            isSaving={isSaving}
          />
        )}

        {step === "assessment-results" && (
          <AssessmentResultsStep
            sessionsData={session}
            studentsData={effectiveStudentData}
            levelsData={levelsData}
            classroomsData={effectiveClassroomData}
            sectionsData={effectiveSectionData}
            totalWords={totalWords}
            incorrectWords={incorrectWords}
            accuracyPercent={accuracyPercent}
            activeCategory={activeCategory}
            fluencyScore={fluencyScore}
            comprehensionScore={comprehensionScore}
            onDecide={handleDecideSession}
            isSaving={isSaving}
          />
        )}
      </div>
    </div>
  );
};

export default SessionEvaluationPage;
